/**
 * Tipografia. Decisão do PO (17/09/2026, lote de aparência 1): **fonte do
 * sistema** — San Francisco no iOS, Roboto no Android, Segoe no Windows.
 * Uma família só; hierarquia por peso e tamanho, não por fonte diferente.
 * Mono só onde há código (release, convite). Zero fonte no bundle: nada é
 * buscado em runtime e o app respeita o tamanho de texto de acessibilidade.
 *
 * Os nomes lógicos (`display`, `ui`, `mono`) continuam existindo para o
 * código não mudar; `display` e `ui` apontam para a mesma pilha.
 */

// Roboto primeiro (refino final, 23/09): no Android é a própria fonte do
// sistema; no iPhone não existe e cai em system-ui/-apple-system (SF).
const SISTEMA = 'Roboto, system-ui, -apple-system, "Segoe UI", "Helvetica Neue", Arial, sans-serif';

export const fontFamily = {
  /** Títulos — mesma família do sistema, pesos maiores. */
  display: 'System',
  /** Interface. */
  ui: 'System',
  /** Dados (horas, valores, datas): a mesma família do sistema — decisão 5. */
  mono: 'System',
  /** Só código de verdade: release do app, código de convite. */
  code: 'Menlo',
} as const;

/**
 * As mesmas famílias como pilha CSS, para a PWA.
 */
export const fontStack = {
  display: SISTEMA,
  ui: SISTEMA,
  mono: SISTEMA,
  code: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
} as const;

/**
 * Escala tipográfica. `minContentLabelFontSize` (12) é o piso para rótulo de
 * conteúdo — abaixo disso só rótulo de campo e de seção em maiúsculas.
 */
export const type = {
  /** Hora do card AGORA. */
  heroClock: { family: fontFamily.display, size: 58, weight: '800', tracking: -0.03 },
  /** Valor da despesa. */
  heroAmount: { family: fontFamily.display, size: 42, weight: '800', tracking: -0.03 },
  /** Título do convite. */
  display40: { family: fontFamily.display, size: 40, weight: '800', tracking: -0.03 },
  display30: { family: fontFamily.display, size: 30, weight: '800', tracking: -0.02 },
  display26: { family: fontFamily.display, size: 26, weight: '800', tracking: -0.02 },
  /** Título de tela e de bottom sheet. */
  title22: { family: fontFamily.display, size: 22, weight: '800', tracking: -0.02 },
  title20: { family: fontFamily.display, size: 20, weight: '800', tracking: -0.02 },

  /** Valor de campo preenchido. */
  fieldValue: { family: fontFamily.ui, size: 15, weight: '700', tracking: 0 },
  /** Título de card. Rodada 3 (A2): 16/800 — o título lê a um braço de distância. */
  cardTitle: { family: fontFamily.ui, size: 16, weight: '800', tracking: 0 },
  /** Era 14 (rodada 2). Rodada 3 (A2, aprovada pelo PO): 15 — leitura em pé, no sol. */
  body: { family: fontFamily.ui, size: 15, weight: '600', tracking: 0 },
  /** Texto de apoio (meta). Era 12.5; A2: 13. */
  support: { family: fontFamily.ui, size: 13, weight: '600', tracking: 0 },
  caption: { family: fontFamily.ui, size: 12, weight: '600', tracking: 0 },
  /** Piso para rótulo de conteúdo: aba, etiqueta de mapa. Era 11/700. */
  tabLabel: { family: fontFamily.ui, size: 12, weight: '800', tracking: 0 },
  /** Só para rótulo de campo em MAIÚSCULAS. */
  fieldLabel: { family: fontFamily.ui, size: 10.5, weight: '700', tracking: 0.06 },

  /** Código de convite. */
  code: { family: fontFamily.mono, size: 30, weight: '600', tracking: 0.08 },
  dataLarge: { family: fontFamily.mono, size: 17, weight: '500', tracking: 0 },
  data: { family: fontFamily.mono, size: 13, weight: '500', tracking: 0 },
  /** Rótulo de seção em MAIÚSCULAS. A2: 11 com tracking .12em (era 10/.14). */
  sectionLabel: { family: fontFamily.mono, size: 11, weight: '600', tracking: 0.12 },
} as const;

export type TypeToken = keyof typeof type;
